import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ArticleViewStats {
  article_id: string;
  view_count: number;
}

export interface TrendingArticle {
  article_id: string;
  view_count: number;
}

// Track article view
export const useTrackArticleView = () => {
  return useMutation({
    mutationFn: async (articleId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('article_views')
        .insert({
          article_id: articleId,
          user_id: user?.id || null,
        });

      if (error) throw error;
    },
  });
};

// Get view count for a specific article
export const useArticleViewCount = (articleId: string) => {
  return useQuery({
    queryKey: ['article-view-count', articleId],
    enabled: !!articleId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('article_views')
        .select('id', { count: 'exact' })
        .eq('article_id', articleId);

      if (error) throw error;
      return data?.length || 0;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

// Get all article view counts
export const useAllArticleViewCounts = () => {
  return useQuery({
    queryKey: ['all-article-view-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('article_views')
        .select('article_id');

      if (error) throw error;

      // Count views per article
      const viewCounts: Record<string, number> = {};
      data.forEach(view => {
        viewCounts[view.article_id] = (viewCounts[view.article_id] || 0) + 1;
      });

      return viewCounts;
    },
    staleTime: 1000 * 60 * 5,
  });
};

// Get trending articles (last 7 days)
export const useTrendingArticles = (days: number = 7) => {
  return useQuery({
    queryKey: ['trending-articles', days],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_trending_articles', {
        days_back: days,
        result_limit: 10
      });

      if (error) throw error;
      
      return (data || []) as TrendingArticle[];
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });
};

// Get view analytics for all articles in last 30 days
export const useArticleAnalytics = () => {
  return useQuery({
    queryKey: ['article-analytics'],
    queryFn: async () => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error } = await supabase
        .from('article_views')
        .select('article_id, viewed_at')
        .gte('viewed_at', thirtyDaysAgo.toISOString())
        .order('viewed_at', { ascending: true });

      if (error) throw error;

      // Group by date
      const dailyViews: Record<string, number> = {};
      const articleViews: Record<string, number> = {};

      data.forEach(view => {
        const date = new Date(view.viewed_at).toLocaleDateString();
        dailyViews[date] = (dailyViews[date] || 0) + 1;
        articleViews[view.article_id] = (articleViews[view.article_id] || 0) + 1;
      });

      return {
        dailyViews,
        articleViews,
        totalViews: data.length,
      };
    },
    staleTime: 1000 * 60 * 15, // Cache for 15 minutes
  });
};
